import React, { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import SelectImport from 'react-dropdown-select'
import { SelectOption } from '../services/base-model'
import { interopDefault } from '../utils/interop'

// react-dropdown-select ships CJS with no `exports` map; the vite 8 (rolldown)
// production bundle exposes its default as `{ default }`, so unwrap it.
const Select = interopDefault(SelectImport)
import { cn } from '../utils/style'
import { computeMenuPlacement, MenuPlacement } from '../utils/dropdown-placement'
import { Input } from './input'
import { Button } from './button'

// Walk up from the control to the first ancestor that actually scrolls (e.g. a
// modal with `overflow-y-auto`). That element — not the window — is what clips
// an open menu, so it's the box we keep the menu inside of.
const findScrollableAncestor = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY
    const scrolls = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    if (scrolls && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }
  return null
}

// Get the props type from the Select component and extend it
type SelectProps = ComponentProps<typeof Select<SelectOption>>

interface CustomSelectProps extends Omit<SelectProps, 'labelField' | 'valueField'> {
  options: SelectOption[]
  values: SelectOption[]
  onChange: (values: SelectOption[]) => void
  className?: string
}

// Separate component for the content renderer to use hooks properly
const DefaultContentRenderer = ({
  props: rendererProps,
  state,
  methods,
}: any): React.ReactElement => {
  const { t } = useTranslation()
  const hasValues = rendererProps.values && rendererProps.values.length > 0
  const isSearchable = rendererProps.searchable
  const isDropdownOpen = state.dropdown
  const shouldFloat = isSearchable && isDropdownOpen && hasValues

  // Ref to measure container width and position for fixed positioning
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)
  const [containerRect, setContainerRect] = React.useState<DOMRect | null>(null)

  React.useEffect(() => {
    if (containerRef.current && shouldFloat) {
      const updateDimensions = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect()
          setContainerWidth(rect.width)
          setContainerRect(rect)
        }
      }
      updateDimensions()

      // Update frequently when floating
      const interval = setInterval(updateDimensions, 100)
      return () => clearInterval(interval)
    } else if (containerRef.current) {
      // Just measure width when not floating
      const updateWidth = () => {
        setContainerWidth(containerRef.current?.offsetWidth || 0)
      }
      updateWidth()

      const resizeObserver = new ResizeObserver(updateWidth)
      resizeObserver.observe(containerRef.current)
      return () => resizeObserver.disconnect()
    }
  }, [shouldFloat])

  // Calculate dynamic sizing based on container width, number of selections, and content length
  const getTagStyles = () => {
    if (!rendererProps.values || rendererProps.values.length === 0) {
      return { maxWidth: 'max-w-32', textSize: 'text-xs', padding: 'px-3 py-1' }
    }

    const valueCount = rendererProps.values.length
    const avgLabelLength =
      rendererProps.values.reduce((sum: number, v: any) => sum + (v.label?.length || 0), 0) /
      valueCount

    // Determine container size category
    const isNarrow = containerWidth > 0 && containerWidth < 300
    const isWide = containerWidth >= 500

    // Narrow containers: Force compact sizing regardless of content
    if (isNarrow) {
      if (valueCount <= 2) {
        return { maxWidth: 'max-w-28', textSize: 'text-xs', padding: 'px-2 py-1' }
      }
      return { maxWidth: 'max-w-20', textSize: 'text-xs', padding: 'px-2 py-0.5' }
    }

    // Wide containers: Can be more generous with space
    if (isWide) {
      // Few selections (1-2) with short labels (< 15 chars): Show more
      if (valueCount <= 2 && avgLabelLength < 15) {
        return { maxWidth: 'max-w-80', textSize: 'text-sm', padding: 'px-4 py-1.5' }
      }

      // Few selections (1-3) with medium labels (15-30 chars): Balanced
      if (valueCount <= 3 && avgLabelLength < 30) {
        return { maxWidth: 'max-w-64', textSize: 'text-sm', padding: 'px-4 py-1.5' }
      }

      // Medium selections (4-6) or longer labels: Compact
      if (valueCount <= 6 || avgLabelLength < 40) {
        return { maxWidth: 'max-w-48', textSize: 'text-xs', padding: 'px-3 py-1' }
      }
    }

    // Standard/medium width containers: Original logic
    // Few selections (1-2) with short labels (< 15 chars): Show more
    if (valueCount <= 2 && avgLabelLength < 15) {
      return { maxWidth: 'max-w-64', textSize: 'text-sm', padding: 'px-4 py-1.5' }
    }

    // Few selections (1-3) with medium labels (15-30 chars): Balanced
    if (valueCount <= 3 && avgLabelLength < 30) {
      return { maxWidth: 'max-w-48', textSize: 'text-sm', padding: 'px-3 py-1' }
    }

    // Medium selections (4-6) or longer labels: Compact
    if (valueCount <= 6 || avgLabelLength < 40) {
      return { maxWidth: 'max-w-32', textSize: 'text-xs', padding: 'px-2.5 py-1' }
    }

    // Many selections (7+) or very long labels: Very compact
    return { maxWidth: 'max-w-24', textSize: 'text-xs', padding: 'px-2 py-0.5' }
  }

  const tagStyles = getTagStyles()

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative min-h-9 w-full',
        shouldFloat ? 'overflow-visible' : 'overflow-hidden',
      )}
    >
      {/* Input area */}
      <div
        className={`flex min-h-9 w-full items-center ${
          isSearchable && isDropdownOpen ? '' : hasValues ? 'px-2' : 'px-2'
        }`}
      >
        {isSearchable && isDropdownOpen ? (
          <Input
            autoFocus
            value={state.search}
            onChange={(value) => methods.setSearch(value)}
            onKeyDown={(e) => {
              // Allow typing and prevent dropdown from closing
              e.stopPropagation()
            }}
            placeholder={hasValues ? t('shared.searchPlaceholder') : rendererProps.placeholder}
            className="!focus:ring-0 !focus:border-0 !border-0 !p-0 !ring-0"
          />
        ) : !hasValues ? (
          <span className="text-sm text-[var(--color-text-placeholder)]">
            {rendererProps.placeholder}
          </span>
        ) : null}
      </div>

      {/* Floating selected values - use fixed positioning when floating to escape overflow */}
      {hasValues && shouldFloat && containerRect && (
        <div
          className="fixed z-dropdown flex items-center transition-all duration-300 ease-out"
          style={{
            top: `${containerRect.top - 60}px`,
            left: `${containerRect.left}px`,
            width: `${containerRect.width}px`,
          }}
        >
          <div className="bg-[var(--color-surface)]/95 scrollbar-none max-h-20 w-full overflow-y-auto rounded-lg border border-[var(--color-border)] p-2 shadow-2xl backdrop-blur-md transition-all duration-300">
            <div className="scrollbar-none flex gap-1 overflow-x-auto">
              {rendererProps.values.map((value: any, index: number) => (
                <span
                  key={index}
                  className={cn(
                    'inline-flex flex-shrink-0 items-center overflow-hidden rounded-full bg-[var(--color-brand-primary)] text-white transition-all',
                    tagStyles.maxWidth,
                    tagStyles.textSize,
                  )}
                >
                  <span className={cn('truncate', tagStyles.padding)}>{value.label}</span>
                  {rendererProps.clearable && !rendererProps.disabled && (
                    <>
                      <div className="bg-surface/20 h-4 w-px" />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          methods.removeItem(e, value, false)
                        }}
                        className="hover:bg-surface/10 h-full rounded-none px-1 py-1 text-white hover:text-white/80"
                      >
                        ×
                      </Button>
                    </>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* When closed, overlay the selected values on top of the placeholder area */}
      {hasValues && !shouldFloat && (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center bg-transparent opacity-100">
          <div className="scrollbar-none max-h-20 w-full overflow-y-auto p-2">
            <div className="scrollbar-none flex gap-1 overflow-x-auto">
              {rendererProps.values.map((value: any, index: number) => (
                <span
                  key={index}
                  className={cn(
                    'inline-flex flex-shrink-0 items-center overflow-hidden rounded-full bg-[var(--color-brand-primary)] text-white transition-all',
                    tagStyles.maxWidth,
                    tagStyles.textSize,
                  )}
                >
                  <span className={cn('truncate', tagStyles.padding)}>{value.label}</span>
                  {rendererProps.clearable && !rendererProps.disabled && (
                    <>
                      <div className="bg-surface/20 h-4 w-px" />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          methods.removeItem(e, value, false)
                        }}
                        className="hover:bg-surface/10 h-full rounded-none px-1 py-1 text-white hover:text-white/80"
                      >
                        ×
                      </Button>
                    </>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const sameSelection = (a: SelectOption[] = [], b: SelectOption[] = []) =>
  a.length === b.length && a.every((option, i) => option.value === b[i]?.value)

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  values,
  onChange,
  className,
  dropdownHeight = '200px',
  contentRenderer,
  searchable = false,
  ...props
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // react-dropdown-select calls onChange with the `values` prop itself whenever that
  // prop changes. Those echoes aren't user changes, and forwarding them lets a stale
  // handler overwrite fresher state (e.g. a form just loaded from the API), so drop them.
  const handleChange = (next: SelectOption[]) => {
    if (sameSelection(next, values)) return
    onChange(next)
  }

  const desiredHeightPx = parseInt(String(dropdownHeight), 10) || 200
  const [placement, setPlacement] = React.useState<MenuPlacement>({
    direction: 'bottom',
    maxHeight: desiredHeightPx,
  })

  // Flip direction / cap height so the open menu stays within its scrollable
  // ancestor's visible area instead of being clipped by the modal's overflow.
  const recomputePlacement = React.useCallback(() => {
    const control = wrapperRef.current?.querySelector(
      '.react-dropdown-select',
    ) as HTMLElement | null
    if (!control) return
    const controlRect = control.getBoundingClientRect()
    const scrollable = findScrollableAncestor(control)
    const containerRect = scrollable
      ? scrollable.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight }
    setPlacement(
      computeMenuPlacement({
        control: { top: controlRect.top, bottom: controlRect.bottom },
        container: { top: containerRect.top, bottom: containerRect.bottom },
        desiredHeight: desiredHeightPx,
      }),
    )
  }, [desiredHeightPx])

  // While open, keep the menu anchored: recompute on open and on any scroll
  // (capture phase catches the modal's inner scroll, which doesn't bubble to
  // window) or resize so it tracks the control rather than lingering.
  React.useLayoutEffect(() => {
    if (!isDropdownOpen) return
    recomputePlacement()
    let raf = 0
    const onViewportChange = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(recomputePlacement)
    }
    window.addEventListener('scroll', onViewportChange, true)
    window.addEventListener('resize', onViewportChange)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onViewportChange, true)
      window.removeEventListener('resize', onViewportChange)
    }
  }, [isDropdownOpen, recomputePlacement])

  // Match the regular Input component styles exactly
  const inputStyleClasses =
    '!w-full !rounded-md !border !border-[var(--color-border)] !bg-[var(--color-surface)] !p-0 !text-[var(--color-text-primary)] placeholder:!font-thin placeholder:!text-[var(--color-text-placeholder)] !outline-none focus:!outline-none focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-[var(--color-border-strong)] invalid:!outline-none invalid:!ring-2 invalid:!ring-[var(--color-error)] disabled:!cursor-not-allowed disabled:!opacity-50'

  React.useEffect(() => {
    // Inject CSS styles for react-dropdown-select
    const styleId = 'custom-select-styles'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        /* Force border override for react-dropdown-select to match Input component */
        .custom-select-wrapper .react-dropdown-select {
          border: 1px solid var(--color-border) !important;
          font-family: inherit !important;
        }

        /* Dropdown styling using Quadrant design system */
        .custom-select-wrapper .react-dropdown-select-dropdown {
          position: absolute;
          left: 0;
          border: 1px solid var(--color-border);
          width: 100%;
          padding: 0;
          display: flex;
          flex-direction: column;
          border-radius: var(--radius-base);
          max-height: var(--dropdown-height);
          overflow: auto;
          z-index: 1;
          background: var(--color-surface);
          box-shadow: var(--shadow-lg);
          color: var(--color-text-primary);
          margin-top: var(--space-1);
          font-family: var(--font-family-sans);
        }

        .custom-select-wrapper .react-dropdown-select-dropdown:focus,
        .custom-select-wrapper .react-dropdown-select-dropdown:focus-visible {
          outline: none !important;
        }

        .custom-select-wrapper .react-dropdown-select-item {
          color: var(--color-text-primary);
          border-bottom: 1px solid var(--color-border);
          padding: var(--space-2) var(--space-3);
          cursor: pointer;
          transition: all var(--transition-fast);
          font-size: var(--font-size-sm);
          background: var(--color-surface);
        }

        .custom-select-wrapper .react-dropdown-select-item:hover {
          background: var(--color-surface-hover);
          color: var(--color-text-primary);
        }

        .custom-select-wrapper .react-dropdown-select-item.react-dropdown-select-item-selected,
        .custom-select-wrapper .react-dropdown-select-item.react-dropdown-select-item-active {
          background: var(--color-brand-primary);
          color: white;
          font-weight: var(--font-weight-medium);
        }

        .custom-select-wrapper .react-dropdown-select-item.react-dropdown-select-item-disabled {
          background: var(--color-surface-hover);
          color: var(--color-text-tertiary);
          cursor: not-allowed;
          opacity: 0.5;
        }

        /* Clear/dropdown handle styling */
        .custom-select-wrapper .react-dropdown-select-clear,
        .custom-select-wrapper .react-dropdown-select-dropdown-handle {
          color: var(--color-text-secondary);
          transition: color var(--transition-fast);
        }

        .custom-select-wrapper .react-dropdown-select-clear:hover,
        .custom-select-wrapper .react-dropdown-select-dropdown-handle:hover {
          color: var(--color-text-primary);
        }

        /* Content area styling */
        .custom-select-wrapper .react-dropdown-select-content {
          padding: 0;
        }

        /* Hide default selected items display since we handle it in contentRenderer */
        .custom-select-wrapper .react-dropdown-select-item-label {
          display: none;
        }

        /* Scrollbar styling for dropdown */
        .custom-select-wrapper .react-dropdown-select-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .custom-select-wrapper .react-dropdown-select-dropdown::-webkit-scrollbar-track {
          background: var(--color-surface);
        }

        .custom-select-wrapper .react-dropdown-select-dropdown::-webkit-scrollbar-thumb {
          background: var(--color-border-strong);
          border-radius: var(--radius-sm);
        }

        .custom-select-wrapper .react-dropdown-select-dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--color-brand-primary);
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <div
      ref={wrapperRef}
      className={`custom-select-wrapper relative ${isDropdownOpen ? 'z-dropdown' : 'z-0'}`}
    >
      <Select<SelectOption>
        options={options}
        values={values}
        onChange={handleChange}
        labelField="label"
        valueField="value"
        contentRenderer={contentRenderer || DefaultContentRenderer}
        searchable={searchable}
        onDropdownOpen={() => setIsDropdownOpen(true)}
        onDropdownClose={() => setIsDropdownOpen(false)}
        className={cn('custom-select', inputStyleClasses, className)}
        {...props}
        dropdownPosition={placement.direction}
        dropdownHeight={`${placement.maxHeight}px`}
      />
    </div>
  )
}
